import {
  autoUpdate,
  computePosition,
  flip,
  shift,
  offset,
  arrow,
} from '@floating-ui/dom';

export class Tooltip {
  constructor(instanceOptions, trigger) {
    if (!trigger) {
      throw new Error('Tooltip requires a trigger element');
    }

    if (!instanceOptions || (!instanceOptions.tooltip && !instanceOptions.tooltipSelector)) {
      throw new Error('Tooltip requires either tooltip text or tooltipSelector');
    }

    if (trigger.getAttribute('data-tooltip-init')) {
      throw new Error('Tooltip already initialized for this element');
    }
    
    this.initializeInstance(instanceOptions, trigger);
    this.makeTriggerAccessible();
    this.createElement();
    this.setupEventListeners();
    this.setupGlobalEventListeners();
  }

  initializeInstance(options, trigger) {
    this.originalTrigger = trigger;
    this.trigger = trigger;
    this.isVisible = false;
    this.isHiding = false;
    this.isActive = false;
    this.tooltip = options.tooltip;
    this.tooltipSelector = options.tooltipSelector;
    this.placement = options.placement || 'top-start';
    this.offset = options.offset || 6;
    this.shiftPadding = options.shiftPadding || 5;
    this.tooltipClass = options.tooltipClass;
    this.tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`;

    this.trigger.setAttribute('data-tooltip-init', 'true');
    Tooltip.instances.push(this);
  }

  makeTriggerAccessible() {
    const focusableElements = 'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';
    const isFocusable = this.trigger.matches(focusableElements) || 
                       this.trigger.tabIndex >= 0;

    this.triggerWasReplaced = false;

    if (!isFocusable) {
      // Store reference to original trigger for potential restoration
      this.originalTriggerParent = this.trigger.parentNode;
      this.originalTriggerNextSibling = this.trigger.nextSibling;
      
      // Convert to button if not already focusable
      const button = document.createElement('button');
      button.type = 'button';
      button.className = this.trigger.className;
      button.innerHTML = this.trigger.innerHTML;
      
      Array.from(this.trigger.attributes).forEach(attr => {
        if (!['class', 'type', 'data-tooltip-init'].includes(attr.name)) {
          button.setAttribute(attr.name, attr.value);
        }
      });
      
      this.trigger.parentNode.replaceChild(button, this.trigger);
      this.trigger = button;
      this.triggerWasReplaced = true;
    }

    this.trigger.setAttribute('aria-describedby', this.tooltipId);
    this.trigger.setAttribute('aria-expanded', 'false');
  }

  createElement() {
    if (this.tooltipSelector) {
      this.element = document.querySelector(this.tooltipSelector);
      if (!this.element) {
        throw new Error(`Tooltip element not found: ${this.tooltipSelector}`);
      }
    } else {
      this.element = document.createElement('div');
      this.createTextElement();
    }
    
    this.removeFromDOM();
    this.setupBaseElement();
    this.createArrowElement();
    this.createCloseElement();
  }

  createTextElement() {
    this.textElement = document.createElement('span');
    this.textElement.classList.add('mt-tooltip-text');
    this.textElement.innerText = this.tooltip;
    this.element.append(this.textElement);
  }

  setupBaseElement() {
    this.element.classList.add('mt-floating', 'mt-tooltip');
    if (this.tooltipClass) {
      this.tooltipClass.split(/\s+/).filter(Boolean).forEach(c => this.element.classList.add(c));
    }
    this.element.setAttribute('role', 'tooltip');
    this.element.setAttribute('id', this.tooltipId);
    this.element.setAttribute('aria-live', 'polite');
    this.element.setAttribute('tabindex', '-1');
  }

  createArrowElement() {
    this.arrowElement = document.createElement('div');
    this.arrowElement.classList.add('mt-floating-arrow');
    this.arrowElement.setAttribute('aria-hidden', 'true');
    this.element.append(this.arrowElement);
  }

  createCloseElement() {
    this.closeElement = document.createElement('button');
    this.closeElement.type = 'button';
    this.closeElement.classList.add('mt-tooltip-close', 'mt-icon', 'mt-icon_x', 'mt-icon_lg');
    this.closeElement.setAttribute('aria-label', 'Close tooltip');
    this.closeElement.setAttribute('tabindex', '0');

    this.boundCloseClick = () => this.hide();
    this.closeListenersActive = false;
  }

  showCloseElement() {
    this.closeElement.addEventListener('click', this.boundCloseClick);
    this.closeListenersActive = true;
    this.element.append(this.closeElement);
  }

  removeCloseElement() {
    if (this.closeElement.parentNode) {
      this.closeElement.parentNode.removeChild(this.closeElement);
    }
  }

  setupEventListeners() {
    this.trigger.style.cursor = 'pointer';
    
    this.boundClick = () => this.click();
    this.boundShow = () => this.show();
    this.boundMouseOff = () => this.mouseOff();
    this.boundKeyDown = (e) => this.handleTriggerKeyDown(e);
    
    this.trigger.addEventListener('click', this.boundClick);
    this.trigger.addEventListener('mouseenter', this.boundShow);
    this.trigger.addEventListener('mouseleave', this.boundMouseOff);
    this.trigger.addEventListener('focus', this.boundShow);
    this.trigger.addEventListener('blur', this.boundMouseOff);
    this.trigger.addEventListener('keydown', this.boundKeyDown);
  }

  setupGlobalEventListeners() {
    this.boundGlobalKeyDown = (e) => this.handleGlobalKeyDown(e);
    this.boundGlobalClick = (e) => this.handleGlobalClick(e);
    
    this.globalListenersActive = false;
  }

  addGlobalEventListeners() {
    if (!this.globalListenersActive) {
      document.addEventListener('keydown', this.boundGlobalKeyDown);
      document.addEventListener('click', this.boundGlobalClick);
      this.globalListenersActive = true;
    }
  }

  removeGlobalEventListeners() {
    if (this.globalListenersActive) {
      document.removeEventListener('keydown', this.boundGlobalKeyDown);
      document.removeEventListener('click', this.boundGlobalClick);
      this.globalListenersActive = false;
    }
  }

  handleTriggerKeyDown(e) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.click();
    } else if (e.key === 'Escape' && this.isVisible) {
      e.preventDefault();
      this.hide();
      this.trigger.focus();
    }
  }

  handleGlobalKeyDown(e) {
    if (!this.isVisible) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      this.hide();
      this.trigger.focus();
    } else if (this.isActive && e.key === 'Tab') {
      this.handleTabNavigation(e);
    }
  }

  handleTabNavigation(e) {
    const focusableElements = this.element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;
    const focusInTooltip = this.element.contains(activeElement);
    
    if (!focusInTooltip) {
      e.preventDefault();
      firstFocusable.focus();
      return;
    }

    if (e.shiftKey) {
      if (activeElement === firstFocusable) {
        e.preventDefault();
        lastFocusable.focus();
      } else {
        const currentIndex = Array.from(focusableElements).indexOf(activeElement);
        if (currentIndex > 0) {
          e.preventDefault();
          focusableElements[currentIndex - 1].focus();
        }
      }
    } else {
      if (activeElement === lastFocusable) {
        e.preventDefault();
        firstFocusable.focus();
      } else {
        const currentIndex = Array.from(focusableElements).indexOf(activeElement);
        if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
          e.preventDefault();
          focusableElements[currentIndex + 1].focus();
        }
      }
    }
  }

  handleGlobalClick(e) {
    if (!this.isVisible) return;
    
    if (!this.element.contains(e.target) && !this.trigger.contains(e.target)) {
      this.hide();
    }
  }

  removeEventListeners() {
    this.trigger.style.cursor = '';
    this.trigger.removeEventListener('click', this.boundClick);
    this.trigger.removeEventListener('mouseenter', this.boundShow);
    this.trigger.removeEventListener('mouseleave', this.boundMouseOff);
    this.trigger.removeEventListener('focus', this.boundShow);
    this.trigger.removeEventListener('blur', this.boundMouseOff);
    this.trigger.removeEventListener('keydown', this.boundKeyDown);

    if (this.closeListenersActive && this.boundCloseClick) {
      this.closeElement.removeEventListener('click', this.boundCloseClick);
      this.closeListenersActive = false;
    }

    this.removeGlobalEventListeners();
  }

  update() {
    return computePosition(this.trigger, this.element, {
      placement: this.placement,
      middleware: [
        offset(this.offset),
        flip(),
        shift({ padding: this.shiftPadding }),
        arrow({ element: this.arrowElement }),
      ],
    }).then(({ x, y, placement, middlewareData }) => {
      this.updateElementPosition(x, y);
      this.updateArrowPosition(placement, middlewareData.arrow);
    }).catch(error => {
      console.error('Failed to update tooltip position:', error);
    });
  }

  updateElementPosition(x, y) {
    Object.assign(this.element.style, {
      left: `${x}px`,
      top: `${y}px`,
    });
  }

  getStaticSide(placement) {
    return {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[placement.split('-')[0]];
  }

  updateArrowPosition(placement, arrowData) {
    if (!arrowData) return;
  
    const { x: arrowX, y: arrowY } = arrowData;
    const staticSide = this.getStaticSide(placement);
    
    Object.assign(this.arrowElement.style, {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: '-0.125rem',
    });
  }

  removeFromDOM() {
    if (this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
  }

  clearActiveStates() {
    this.element.classList.remove('mt-tooltip_clicked');
    this.trigger.classList.remove('active');
  }

  startAutoUpdate() {
    this.cleanup = autoUpdate(
      this.trigger,
      this.element,
      () => {
        this.update();
      },
    );
  }

  stopAutoUpdate() {
    if (this.cleanup) {
      this.cleanup();
    }
  }

  show() {
    if (this.isVisible || this.isHiding) return;
    
    document.body.append(this.element);
    this.element.style.display = 'block';
    this.isVisible = true;
    this.trigger.setAttribute('aria-expanded', 'true');
    
    this.update();
    this.startAutoUpdate();
    this.addGlobalEventListeners();
  }

  hide() {
    if (!this.isVisible || this.isHiding) return;

    this.trigger.setAttribute('aria-expanded', 'false');
    if (this.isActive) {
      this.trigger.focus();
    }
    
    this.isHiding = true;
    this.isActive = false;
    this.isVisible = false;
    this.element.style.display = '';
    
    this.removeCloseElement();
    this.removeFromDOM();
    this.clearActiveStates();
    this.stopAutoUpdate();
    this.removeGlobalEventListeners();

    setTimeout(() => {
      this.isHiding = false;
    }, 10);
  }

  click() {
    this.isActive = true;
    this.trigger.classList.add('active');
    this.element.classList.add('mt-tooltip_clicked');
    this.showCloseElement();
    this.show();
  }

  mouseOff() {
    if (!this.isActive) {
      this.hide();
    }
  }

  destroy() {
    this.hide();
    this.removeEventListeners();
    this.trigger.removeAttribute('data-tooltip-init');
    this.trigger.removeAttribute('aria-describedby');
    this.trigger.removeAttribute('aria-expanded');
    
    // If the trigger was replaced with a <button> for accessibility, revert to the original element
    if (this.triggerWasReplaced && this.originalTrigger && this.originalTriggerParent) {
      try {
        if (this.originalTriggerNextSibling) {
          this.originalTriggerParent.insertBefore(this.originalTrigger, this.originalTriggerNextSibling);
        } else {
          this.originalTriggerParent.appendChild(this.originalTrigger);
        }
        
        if (this.trigger.parentNode) {
          this.trigger.parentNode.removeChild(this.trigger);
        }
      } catch (error) {
        console.warn('Failed to restore original trigger element:', error);
      }
    }
    
    const index = Tooltip.instances.indexOf(this);
    if (index > -1) {
      Tooltip.instances.splice(index, 1);
    }
  }
  
  updateText(text) {
    this.tooltip = text;
    if (this.textElement) {
      this.textElement.innerText = text;
    }
  }
}

Tooltip.instances = [];

Tooltip.hideAll = function() {
  Tooltip.instances.forEach(instance => instance.hide());
}

Tooltip.destroyAll = function() {
  Tooltip.instances.forEach(instance => instance.destroy());
}

Tooltip.getInstance = function(triggerElement) {
  return Tooltip.instances.find(instance => 
    instance.trigger === triggerElement || instance.originalTrigger === triggerElement
  );
}

if (typeof window !== 'undefined') {
  window.Tooltip = Tooltip;
}

export default Tooltip;
